/**
 * Scheme allowlist for links built from data we did not author.
 *
 * Form submission values arrive from a public webhook, and the ingest endpoint
 * deliberately stores values that fail validation rather than dropping a real
 * enquiry. That means a `javascript:` or `data:` payload can reach the admin's
 * screen, so the render side has to be the thing that refuses to linkify it —
 * validity is not a safety property here.
 *
 * Returns the URL when it is safe to put in an href, or null to render the raw
 * text instead.
 */
const SAFE_SCHEMES = ['http:', 'https:'];

export function safeHref(value: unknown): string | null {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (trimmed === '') return null;

  // A protocol-relative or relative URL has no scheme to vet, and nothing here
  // should be producing one, so treat it as untrusted.
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }

  return SAFE_SCHEMES.includes(parsed.protocol) ? trimmed : null;
}

/** Very loose shape check — enough to refuse a scheme or a newline. */
export function safeMailto(value: unknown): string | null {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();

  return /^[^\s:@]+@[^\s:@]+\.[^\s:@]+$/.test(trimmed) ? `mailto:${trimmed}` : null;
}

/** Digits, spaces and the usual punctuation only; no scheme, no newline. */
export function safeTel(value: unknown): string | null {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();

  return /^[+()\-.\s\d]{3,32}$/.test(trimmed) ? `tel:${trimmed.replace(/\s+/g, '')}` : null;
}
